import React, { useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ChevronLeft, MessageSquare, Volume2, ChevronRight,
  User, Users, CheckCircle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Roleplay scenarios - EXPANDED CONTENT (100+ dialogues)
const SCENARIOS = [
  {
    id: 'greeting',
    name: 'Yolcu Karşılama',
    description: 'Uçağa binen yolcuları karşılama',
    icon: '👋',
    dialogue: [
      { speaker: 'crew', text: "Good morning! Welcome aboard.", text_tr: "Günaydın! Uçağımıza hoş geldiniz.", isPrompt: true },
      { speaker: 'passenger', text: "Thank you. Where is seat 15A?", text_tr: "Teşekkürler. 15A koltuğu nerede?" },
      { speaker: 'crew', text: "It's on the left side, by the window. Let me help you with your bag.", text_tr: "Sol tarafta, pencere kenarında. Çantanızla yardım edeyim.", isPrompt: true },
      { speaker: 'passenger', text: "That's very kind of you.", text_tr: "Çok naziksiniz." },
      { speaker: 'crew', text: "Please let me know if you need anything during the flight.", text_tr: "Uçuş sırasında herhangi bir şeye ihtiyacınız olursa lütfen bana bildirin.", isPrompt: true },
      { speaker: 'passenger', text: "I will, thank you!", text_tr: "Söylerim, teşekkürler!" }
    ]
  },
  {
    id: 'service',
    name: 'İkram Servisi',
    description: 'Yemek ve içecek servisi',
    icon: '🍽️',
    dialogue: [
      { speaker: 'crew', text: "Would you like something to drink?", text_tr: "İçecek ister misiniz?", isPrompt: true },
      { speaker: 'passenger', text: "Yes, what do you have?", text_tr: "Evet, neler var?" },
      { speaker: 'crew', text: "We have coffee, tea, juice, and soft drinks.", text_tr: "Kahve, çay, meyve suyu ve meşrubat var.", isPrompt: true },
      { speaker: 'passenger', text: "I'll have orange juice, please.", text_tr: "Portakal suyu lütfen." },
      { speaker: 'crew', text: "Here you go. Would you like some snacks as well?", text_tr: "Buyrun. Atıştırmalık da ister misiniz?", isPrompt: true },
      { speaker: 'passenger', text: "Yes, what do you have?", text_tr: "Evet, neler var?" },
      { speaker: 'crew', text: "We have cookies, pretzels, and mixed nuts.", text_tr: "Kurabiye, kraker ve karışık kuruyemiş var.", isPrompt: true },
      { speaker: 'passenger', text: "I'll have the cookies, please.", text_tr: "Kurabiye lütfen." }
    ]
  },
  {
    id: 'complaint',
    name: 'Şikayet Yönetimi',
    description: 'Mutsuz yolcu ile iletişim',
    icon: '😤',
    dialogue: [
      { speaker: 'passenger', text: "Excuse me, my seat won't recline!", text_tr: "Pardon, koltuğum yatmıyor!" },
      { speaker: 'crew', text: "I'm sorry to hear that. Let me check it for you.", text_tr: "Üzgünüm. Sizin için kontrol edeyim.", isPrompt: true },
      { speaker: 'passenger', text: "I paid extra for this seat!", text_tr: "Bu koltuk için ekstra ödedim!" },
      { speaker: 'crew', text: "I completely understand your frustration. Let me see if we have another seat available.", text_tr: "Hayal kırıklığınızı tamamen anlıyorum. Başka bir koltuk var mı bakayım.", isPrompt: true },
      { speaker: 'passenger', text: "This is unacceptable!", text_tr: "Bu kabul edilemez!" },
      { speaker: 'crew', text: "I sincerely apologize for this inconvenience. I've found a better seat for you in row 10. Would you like to move?", text_tr: "Bu rahatsızlık için içtenlikle özür dilerim. 10. sırada sizin için daha iyi bir koltuk buldum. Taşınmak ister misiniz?", isPrompt: true },
      { speaker: 'passenger', text: "Yes, please. Thank you for understanding.", text_tr: "Evet, lütfen. Anlayışınız için teşekkürler." }
    ]
  },
  {
    id: 'safety',
    name: 'Güvenlik Uyarısı',
    description: 'Emniyet kemeri uyarısı',
    icon: '⚠️',
    dialogue: [
      { speaker: 'crew', text: "Excuse me, sir. We're experiencing turbulence. Please fasten your seatbelt.", text_tr: "Pardon efendim. Türbülans yaşıyoruz. Lütfen emniyet kemerinizi bağlayın.", isPrompt: true },
      { speaker: 'passenger', text: "But I need to use the restroom.", text_tr: "Ama tuvalete gitmem lazım." },
      { speaker: 'crew', text: "I understand, but for your safety, please remain seated until the seatbelt sign is turned off.", text_tr: "Anlıyorum, ama güvenliğiniz için lütfen emniyet kemeri işareti sönene kadar yerinizde kalın.", isPrompt: true },
      { speaker: 'passenger', text: "How long will it take?", text_tr: "Ne kadar sürecek?" },
      { speaker: 'crew', text: "The captain expects it to last about 10 minutes. I'll let you know as soon as it's safe.", text_tr: "Kaptan yaklaşık 10 dakika sürmesini bekliyor. Güvenli olduğunda size hemen bildireceğim.", isPrompt: true },
      { speaker: 'passenger', text: "Okay, I'll wait.", text_tr: "Tamam, bekleyeceğim." }
    ]
  },
  {
    id: 'medical',
    name: 'Tıbbi Yardım',
    description: 'Sağlık sorunu olan yolcu',
    icon: '🏥',
    dialogue: [
      { speaker: 'passenger', text: "Excuse me, I'm not feeling well.", text_tr: "Pardon, kendimi iyi hissetmiyorum." },
      { speaker: 'crew', text: "I'm sorry to hear that. Can you tell me what's wrong?", text_tr: "Üzgünüm. Bana sorunun ne olduğunu söyleyebilir misiniz?", isPrompt: true },
      { speaker: 'passenger', text: "I feel dizzy and nauseous.", text_tr: "Başım dönüyor ve midem bulanıyor." },
      { speaker: 'crew', text: "Let me get you some water and a sick bag. Are you diabetic or do you have any medical conditions?", text_tr: "Size biraz su ve torba getireyim. Diyabetiniz veya herhangi bir sağlık sorununuz var mı?", isPrompt: true },
      { speaker: 'passenger', text: "No, I think it might be motion sickness.", text_tr: "Hayır, sanırım hareket hastalığı olabilir." },
      { speaker: 'crew', text: "I'll bring you some medication that can help. Please recline your seat and focus on the horizon if you can see outside.", text_tr: "Size yardımcı olabilecek bir ilaç getireceğim. Lütfen koltuğunuzu yatırın ve dışarıyı görebiliyorsanız ufka odaklanın.", isPrompt: true },
      { speaker: 'passenger', text: "Thank you so much for your help.", text_tr: "Yardımınız için çok teşekkürler." }
    ]
  },
  {
    id: 'lost_item',
    name: 'Kayıp Eşya',
    description: 'Eşyasını kaybeden yolcu',
    icon: '🔍',
    dialogue: [
      { speaker: 'passenger', text: "Excuse me, I can't find my passport!", text_tr: "Pardon, pasaportumu bulamıyorum!" },
      { speaker: 'crew', text: "Please don't worry, let's search together. When did you last see it?", text_tr: "Lütfen endişelenmeyin, birlikte arayalım. En son ne zaman gördünüz?", isPrompt: true },
      { speaker: 'passenger', text: "I had it during boarding.", text_tr: "Binişte elimdeydi." },
      { speaker: 'crew', text: "Could it be in your jacket pocket or in the seat pocket in front of you?", text_tr: "Ceket cebinizde veya önünüzdeki koltuk cebinde olabilir mi?", isPrompt: true },
      { speaker: 'passenger', text: "Let me check... Oh, here it is in my jacket!", text_tr: "Bakayım... Ah, ceketimde buldum!" },
      { speaker: 'crew', text: "I'm glad you found it! I recommend keeping important documents in a secure place during the flight.", text_tr: "Bulduğunuza sevindim! Uçuş sırasında önemli belgeleri güvenli bir yerde tutmanızı öneririm.", isPrompt: true }
    ]
  },
  {
    id: 'special_meal',
    name: 'Özel Yemek Talebi',
    description: 'Özel diyet ihtiyacı',
    icon: '🥗',
    dialogue: [
      { speaker: 'passenger', text: "I ordered a vegetarian meal, but I received chicken.", text_tr: "Vejetaryen yemek sipariş ettim ama tavuk geldi." },
      { speaker: 'crew', text: "I apologize for the mix-up. Let me check our records and find your special meal.", text_tr: "Karışıklık için özür dilerim. Kayıtlarımızı kontrol edip özel yemeğinizi bulayım.", isPrompt: true },
      { speaker: 'passenger', text: "I specifically requested it when I booked.", text_tr: "Rezervasyon yaparken özellikle belirttim." },
      { speaker: 'crew', text: "I understand, and I'm truly sorry. I found your vegetarian meal. Here it is.", text_tr: "Anlıyorum ve gerçekten üzgünüm. Vejetaryen yemeğinizi buldum. Buyrun.", isPrompt: true },
      { speaker: 'passenger', text: "Thank you. I appreciate your help.", text_tr: "Teşekkürler. Yardımınız için minnettarım." },
      { speaker: 'crew', text: "Is there anything else I can get for you? Perhaps some extra bread or fruit?", text_tr: "Size başka bir şey getirebilir miyim? Belki ekstra ekmek veya meyve?", isPrompt: true }
    ]
  },
  {
    id: 'nervous_flyer',
    name: 'Uçuştan Korkan Yolcu',
    description: 'Kaygılı yolcuyu sakinleştirme',
    icon: '😰',
    dialogue: [
      { speaker: 'passenger', text: "I'm really scared of flying. This is my first time.", text_tr: "Uçmaktan çok korkuyorum. Bu ilk seferim." },
      { speaker: 'crew', text: "I understand. Flying can feel overwhelming at first, but you're in very safe hands.", text_tr: "Anlıyorum. Uçmak başta bunaltıcı hissettirebilir, ama çok güvenli ellerdesiniz.", isPrompt: true },
      { speaker: 'passenger', text: "What if something goes wrong?", text_tr: "Ya bir şeyler ters giderse?" },
      { speaker: 'crew', text: "Flying is actually the safest form of transportation. Our pilots are highly trained, and the aircraft is checked before every flight.", text_tr: "Uçmak aslında en güvenli ulaşım şeklidir. Pilotlarımız çok iyi eğitimlidir ve uçak her uçuştan önce kontrol edilir.", isPrompt: true },
      { speaker: 'passenger', text: "That makes me feel a bit better.", text_tr: "Bu beni biraz rahatlattı." },
      { speaker: 'crew', text: "I'll check on you regularly during the flight. If you feel anxious, just press the call button and I'll be right here.", text_tr: "Uçuş sırasında sizi düzenli olarak kontrol edeceğim. Kaygılı hissederseniz çağrı düğmesine basın, hemen yanınızda olacağım.", isPrompt: true },
      { speaker: 'passenger', text: "Thank you, that means a lot to me.", text_tr: "Teşekkürler, bu benim için çok önemli." }
    ]
  },
  {
    id: 'upgrade_request',
    name: 'Sınıf Yükseltme Talebi',
    description: 'Business class yükseltme isteği',
    icon: '⬆️',
    dialogue: [
      { speaker: 'passenger', text: "Is it possible to upgrade to business class?", text_tr: "Business class'a yükseltme yapılabilir mi?" },
      { speaker: 'crew', text: "Let me check availability for you. May I see your boarding pass?", text_tr: "Müsaitlik durumunu sizin için kontrol edeyim. Biniş kartınızı görebilir miyim?", isPrompt: true },
      { speaker: 'passenger', text: "Here you go. I'm a frequent flyer.", text_tr: "Buyrun. Sık uçan yolcuyum." },
      { speaker: 'crew', text: "Thank you. I can see you have Gold status. Unfortunately, business class is fully booked today, but I can offer you an emergency exit row seat with extra legroom.", text_tr: "Teşekkürler. Gold statünüz olduğunu görüyorum. Maalesef bugün business class tamamen dolu, ama size ekstra bacak mesafesi olan acil çıkış sırası koltuğu sunabilirim.", isPrompt: true },
      { speaker: 'passenger', text: "That would be great, thank you!", text_tr: "Harika olur, teşekkürler!" }
    ]
  },
  {
    id: 'child_assistance',
    name: 'Çocuklu Aile',
    description: 'Çocuklu aileye yardım',
    icon: '👨‍👩‍👧',
    dialogue: [
      { speaker: 'passenger', text: "My child is getting restless. Do you have anything to keep her entertained?", text_tr: "Çocuğum huzursuzlanmaya başladı. Onu meşgul edecek bir şeyiniz var mı?" },
      { speaker: 'crew', text: "Of course! We have coloring books, puzzles, and games especially for children. Would she like any of these?", text_tr: "Tabii ki! Özellikle çocuklar için boyama kitapları, bulmacalar ve oyunlarımız var. Bunlardan herhangi birini ister mi?", isPrompt: true },
      { speaker: 'passenger', text: "The coloring book sounds great!", text_tr: "Boyama kitabı harika olur!" },
      { speaker: 'crew', text: "Here you go. I also brought some crayons. If she gets hungry, I can bring a kid-friendly snack.", text_tr: "Buyrun. Biraz da boya kalemi getirdim. Acıkırsa çocuklara uygun bir atıştırmalık getirebilirim.", isPrompt: true },
      { speaker: 'passenger', text: "You're so thoughtful, thank you!", text_tr: "Çok düşüncelisiniz, teşekkürler!" }
    ]
  },
  {
    id: 'connecting_flight',
    name: 'Aktarmalı Uçuş',
    description: 'Bağlantılı uçuş bilgisi',
    icon: '🔄',
    dialogue: [
      { speaker: 'passenger', text: "I have a connecting flight to Paris. Will I make it?", text_tr: "Paris'e aktarmalı uçuşum var. Yetişir miyim?" },
      { speaker: 'crew', text: "Let me check the arrival time and your connection. What time is your flight to Paris?", text_tr: "Varış saatini ve bağlantınızı kontrol edeyim. Paris uçuşunuz saat kaçta?", isPrompt: true },
      { speaker: 'passenger', text: "It's at 4:30 PM.", text_tr: "Saat 16:30'da." },
      { speaker: 'crew', text: "We're scheduled to land at 3:15 PM, which gives you over an hour. I'll make sure you're one of the first to disembark.", text_tr: "15:15'te inmemiz planlanıyor, bu size bir saatten fazla zaman veriyor. İlk inenlerden olmanızı sağlayacağım.", isPrompt: true },
      { speaker: 'passenger', text: "Thank you! What gate will my Paris flight be?", text_tr: "Teşekkürler! Paris uçuşum hangi kapıdan olacak?" },
      { speaker: 'crew', text: "I'll get that information from the captain and let you know before landing.", text_tr: "Bu bilgiyi kaptandan alacağım ve inişten önce size bildireceğim.", isPrompt: true }
    ]
  },
  {
    id: 'elderly_assistance',
    name: 'Yaşlı Yolcu Yardımı',
    description: 'Yaşlı yolcuya özel ilgi',
    icon: '👴',
    dialogue: [
      { speaker: 'crew', text: "Good afternoon. Can I help you get settled?", text_tr: "İyi günler. Yerleşmenize yardımcı olabilir miyim?", isPrompt: true },
      { speaker: 'passenger', text: "Yes please, I have trouble with my mobility.", text_tr: "Evet lütfen, hareket etmekte zorlanıyorum." },
      { speaker: 'crew', text: "No problem at all. Let me store your bag and help you with your seatbelt.", text_tr: "Hiç sorun değil. Çantanızı koyayım ve emniyet kemerinizle yardımcı olayım.", isPrompt: true },
      { speaker: 'passenger', text: "Thank you, dear. You're very kind.", text_tr: "Teşekkürler canım. Çok naziksin." },
      { speaker: 'crew', text: "Is there anything else you need? A blanket or pillow perhaps?", text_tr: "Başka ihtiyacınız olan bir şey var mı? Belki battaniye veya yastık?", isPrompt: true },
      { speaker: 'passenger', text: "A blanket would be lovely.", text_tr: "Bir battaniye güzel olur." },
      { speaker: 'crew', text: "Here you go. I'll be checking on you throughout the flight. Don't hesitate to call me.", text_tr: "Buyrun. Uçuş boyunca sizi kontrol edeceğim. Beni çağırmaktan çekinmeyin.", isPrompt: true }
    ]
  }
];

export default function CabinCrewRoleplay() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [completedLines, setCompletedLines] = useState([]);
  const [audioPlaying, setAudioPlaying] = useState(false);
  
  const audioRef = useRef(null);

  // Demo kullanıcılar için sadece 'greeting' (Yolcu Karşılama) senaryosu
  const isDemo = user?.is_demo || false;
  const demoAllowedScenarios = ['greeting'];

  const currentScenario = SCENARIOS.find(s => s.id === selectedScenario);
  const currentLine = currentScenario?.dialogue[currentLineIndex];

  const playAudio = async (text) => {
    if (!text) return;
    
    // First try browser TTS as primary (OpenAI rate limited)
    if ('speechSynthesis' in window) {
      try {
        setAudioPlaying(true);
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.onend = () => setAudioPlaying(false);
        utterance.onerror = () => setAudioPlaying(false);
        speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.error('Browser TTS failed:', e);
      }
    }
    
    // Fallback to API if browser TTS not available
    try {
      setAudioPlaying(true);
      const response = await axios.post(
        `${API_URL}/api/speaking/text-to-speech`,
        { text },
        { responseType: 'blob' }
      );
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('audio')) {
        const audioUrl = URL.createObjectURL(response.data);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('Audio API error:', error);
    } finally {
      setAudioPlaying(false);
    }
  };

  const handleNext = () => {
    if (!completedLines.includes(currentLineIndex)) {
      setCompletedLines([...completedLines, currentLineIndex]);
    }
    
    if (currentLineIndex < currentScenario.dialogue.length - 1) {
      setCurrentLineIndex(currentLineIndex + 1);
    } else {
      // Senaryo tamamlandı - ana sayfaya dön
      toast.success('Senaryo tamamlandı! 🎉');
      setTimeout(() => {
        setSelectedScenario(null);
        setCurrentLineIndex(0);
        setCompletedLines([]);
      }, 1500);
    }
  };

  // Scenario selection
  if (!selectedScenario) {
    return (
        <div className="min-h-screen bg-gray-50">
          <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
          
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-teal-500 pb-8 rounded-b-[2rem]">
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <button
                onClick={() => navigate('/english-training')}
                className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium">Geri</span>
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                  <MessageSquare className="w-8 h-8 text-cyan-500" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Rol-Play Simülasyonu</h1>
                  <p className="text-cyan-100 text-sm">Yolcu ile etkileşim pratiği</p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SCENARIOS.map((scenario) => {
                const isLocked = isDemo && !demoAllowedScenarios.includes(scenario.id);
                return (
                <Card 
                  key={scenario.id}
                  className={`transition-all border-0 shadow-lg ${
                    isLocked 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:ring-2 hover:ring-cyan-300'
                  }`}
                  onClick={() => {
                    if (!isLocked) {
                      setSelectedScenario(scenario.id);
                      setCurrentLineIndex(0);
                      setCompletedLines([]);
                    }
                  }}
                >
                  <CardContent className="p-6 relative">
                    {isLocked && (
                      <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full">
                        🔒 Premium
                      </div>
                    )}
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 bg-gradient-to-br ${isLocked ? 'from-gray-400 to-gray-500' : 'from-cyan-400 to-teal-500'} rounded-xl flex items-center justify-center`}>
                        <span className="text-3xl">{scenario.icon}</span>
                      </div>
                      <div>
                        <h3 className={`text-xl font-bold ${isLocked ? 'text-gray-500' : 'text-gray-800'}`}>{scenario.name}</h3>
                        <p className={`text-sm ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                          {isLocked ? 'Tam sürümde mevcut' : scenario.description}
                        </p>
                      </div>
                    </div>
                    {!isLocked && (
                      <Badge className="bg-cyan-500 text-white">
                        {scenario.dialogue.length} Diyalog
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              )})}
          </div>
        </div>
      </div>
      
    );
  }

  // Roleplay screen
  return (
    
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-emerald-50 p-4">
        <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
        
        <div className="container mx-auto max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => setSelectedScenario(null)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="text-sm text-gray-500">{currentScenario?.name}</p>
              <p className="font-bold">{currentLineIndex + 1} / {currentScenario?.dialogue.length}</p>
            </div>
            <Badge className="bg-cyan-500 text-white">{currentScenario?.icon}</Badge>
          </div>

        <Progress 
          value={((currentLineIndex + 1) / currentScenario?.dialogue.length) * 100}
          className="h-2 mb-6"
        />

        {/* Dialogue history */}
        <div className="space-y-3 mb-6">
          {currentScenario?.dialogue.slice(0, currentLineIndex + 1).map((line, idx) => (
            <div
              key={idx}
              className={`flex ${line.speaker === 'crew' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-2xl p-4 ${
                line.speaker === 'crew'
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-br-none'
                  : 'bg-white border-2 border-gray-200 rounded-bl-none'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {line.speaker === 'crew' ? (
                    <User className="w-4 h-4" />
                  ) : (
                    <Users className="w-4 h-4" />
                  )}
                  <span className="text-xs font-bold opacity-75">
                    {line.speaker === 'crew' ? 'Kabin Memuru' : 'Yolcu'}
                  </span>
                  {idx === currentLineIndex && (
                    <button
                      onClick={() => playAudio(line.text)}
                      disabled={audioPlaying}
                      className="ml-auto"
                    >
                      {audioPlaying ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Volume2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
                <p className="font-medium">{line.text}</p>
                <p className={`text-xs mt-1 ${line.speaker === 'crew' ? 'opacity-75' : 'text-gray-500'}`}>
                  {line.text_tr}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Action button */}
        <Button
          onClick={handleNext}
          className="w-full bg-gradient-to-r from-cyan-500 to-teal-500"
        >
          {currentLineIndex === currentScenario.dialogue.length - 1 ? (
            <>Tamamla <CheckCircle className="w-4 h-4 ml-2" /></>
          ) : (
            <>Devam Et <ChevronRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
    
  );
}
